/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { AutocompleteItemDto, ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  optimisticLockingOf,
  RequestMethod,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { Observable } from 'rxjs';
import { CheckUsageDto } from '../common/check-usage.dto';
import { CheckUsageMapper } from '../common/check-usage.mapper';
import { CSMasterDetailMapper, CSMasterMapper } from './models/cs-master.mapper';
import {
  CSMasterDetailsDto,
  CsMasterFilterDto,
  CSMasterListItemDto,
} from './models/cs-master.model';

@Injectable()
export class CsMasterRestService extends AbstractRestService {
  constructor(settings: RestSettings) {
    super(settings);
  }

  create(detail: CSMasterDetailsDto): Observable<CSMasterDetailsDto> {
    return this.httpPost(
      `${this.bp}/color-selection-master`,
      detail,
      CSMasterDetailMapper);
  }

  checkUsage(item: CSMasterListItemDto): Observable<CheckUsageDto> {
    const { id, version } = item;
    return this.httpGet(
      `${this.bp}/color-selection-master/check-usage/${id}`,
      { version },
      void 0,
      CheckUsageMapper,
    );
  }

  cloneItem(item: AutocompleteItemDto): Observable<any> {
    return this.httpPost(`${this.bp}/job-admin/clone`, item, CSMasterMapper);
  }

  deactivateOrDelete(item: CSMasterListItemDto, checkUsage: CheckUsageDto): Observable<CheckUsageDto> {
    return this.httpDelete(
      `${this.bp}/color-selection-master/${item.id}`, checkUsage, CheckUsageMapper);
  }

  get(id: ID): Observable<CSMasterDetailsDto> {
    return this.httpGet(
      `${this.bp}/color-selection-master/${id}`,
      CSMasterDetailMapper,
    );
  }

  cloneFromCsMaster(id: ID): Observable<CSMasterDetailsDto> {
    return this.httpGet(
      `${this.bp}/color-selection-master/${id}/clone`,
      CSMasterDetailMapper,
    );
  }

  cloneFromJobCs(id: ID): Observable<CSMasterDetailsDto> {
    return this.httpGet(
      `${this.bp}/jobs/${id}/color-selection-master`,
      CSMasterDetailMapper,
    );
  }

  undelete(item: CSMasterListItemDto): Observable<CSMasterListItemDto> {
    return this.httpPut(
      `${this.bp}/color-selection-master/undelete/${item.id}`, void 0, optimisticLockingOf(CSMasterMapper));
  }

  findAllAsFinderDataSource(filter: CsMasterFilterDto): SlicedDataSource<AutocompleteItemDto> {
    return this.createSlicedDataSource({
      method: RequestMethod.Get,
      url: `${this.bp}/color-selection-master/finder`,
      body: filter,
      responseMapper: arrayOf(CSMasterMapper),
    });
  }

  findAllAsDataSource(filter: CsMasterFilterDto): SlicedDataSource<CSMasterListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/color-selection-master`,
      filter,
      void 0,
      arrayOf(CSMasterMapper));
  }

  update(detail: CSMasterDetailsDto): Observable<CSMasterDetailsDto> {
    return this.httpPut(`${this.bp}/color-selection-master/${detail.csMasterId}`, detail, CSMasterDetailMapper);
  }
}
